import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components/macro';
import { useParams, useHistory } from 'react-router-dom'
import { useUserContext } from 'context/UserContext';
import { useEditorContext, actions } from 'context/EditorContext';
import { Icon, Button } from 'components/atoms';
import { Tooltip } from 'components/molecules';
import { themeType } from 'types'
import { PageAddModuleModal, Toast } from 'containers'
import { produce } from 'immer'
import { useDispatch } from 'react-redux'
import { saveDraft } from 'redux/draftsSlice'
import { useToggle } from 'hooks'
import { toast } from 'react-toastify';
import { generateEditorPath } from 'services/generators'
import ModuleActionItem from './ModuleActionItem'

const ModuleActions = ({
  onEdit,
  onCloseEdit,
  isEditing,
  onMoveUp,
  onMoveDown,
  onDelete,
  theme,
  isFirst,
  isLast,
  sectionActive,
  onCopy,
}) => {

  const [isOpen, toggle] = useToggle(false)
  const { pageId, lang, subweb } = useParams()
  const history = useHistory()

  const [{ user }] = useUserContext()
  const [{ pageLiveData, pageTitle }, dispatch] = useEditorContext();
  const dispatchRedux = useDispatch()

  const subwebData = useMemo(
    () => user.subwebs.find(({ slug }) => slug === subweb || encodeURIComponent(slug) === subweb),
    [subweb, user]
  )

  const moduleShouldUseActiveColor = ['Agenda', 'SubWebLinks', 'Contests']

  const handleAddModule = (module) => {
    if (
      module
      && module.options
      && 'getDefault' in module.options
      && typeof module.options.getDefault === 'function'
    ) {
      let newModuleProps = module.options.getDefault({ lang })

      // article middleware
      // adds title if first article and active color from subweb
      if (module.key === 'Article') {
        // check if is first article
        const alreadyHasArticle = pageLiveData.find(module => module.name === 'Article')
        const opts = { activeColor: subwebData.color }
        // in that case add the title
        if (!alreadyHasArticle) {
          newModuleProps = module.options.getDefault({
            lang,
            title: pageTitle,
            ...opts
          })
        }
        newModuleProps = module.options.getDefault({ lang, ...opts })
      }

      // agenda, contests, SubWebLinks middleware
      if (moduleShouldUseActiveColor.includes(module.key) && subwebData && subwebData.color) {
        newModuleProps = module.options.getDefault({ lang, color: subwebData.color })
      }

      const arraySplitIndex = Number(sectionActive.split('-')[1] ?? 0)

      const pageWithModule = produce(pageLiveData, _ => (
        // eslint-disable-next-line no-param-reassign
        [
          ...pageLiveData.slice(0, arraySplitIndex),
          newModuleProps,
          ...pageLiveData.slice(arraySplitIndex)
        ]
      ))

      dispatch({
        type: actions.PAGE_DATA_UPDATE,
        data: pageWithModule,
      });

      dispatchRedux(saveDraft({ pageId, content: pageWithModule, lang }))
      const moduleId = `${module.key}-${arraySplitIndex}`
      history.push(generateEditorPath({
        lang, pageId, subweb, moduleId
      }))

      toast.success(
        <Toast
          type="success"
          title={`${module.options.label} adicionado`}
          description="Edite o módulo antes de gravar"
        />
      );
    }
  }

  const newModuleAboveButton = useMemo(() => {
    const tooltip = 'Novo módulo'
    const modifiers = ['removeBorders']
    const iconColor = theme.colors.base
    const icon = 'fal fa-plus'
    return {
      tooltip,
      modifiers,
      icon,
      iconColor
    }
  }, [isEditing, theme.colors.baseInverse, theme.colors.primary])

  const editingButton = useMemo(() => {
    const tooltip = isEditing ? 'concluir edição' : 'editar'
    const modifiers = isEditing ? ['primary', 'removeBorders'] : ['removeBorders']
    const iconColor = isEditing ? theme.colors.baseInverse : theme.colors.primary
    const icon = isEditing ? 'fal fa-check' : 'fal fa-pen'
    return {
      tooltip,
      modifiers,
      icon,
      iconColor
    }
  }, [isEditing, theme.colors.baseInverse, theme.colors.primary])

  const copyButton = useMemo(() => {
    const tooltip = 'copiar módulo'
    const modifiers = ['removeBorders']
    const iconColor = theme.colors.base
    return {
      tooltip,
      modifiers,
      icon: 'fal fa-clone',
      iconColor,
    }
  }, [theme.colors.base, theme.colors.baseLighterS])

  const moveUpButton = useMemo(() => {
    const tooltip = isFirst ? null : 'mover para cima'
    const modifiers = isFirst ? ['removeBorders', 'disabled'] : ['removeBorders']
    const iconColor = isFirst ? theme.colors.baseLighterS : theme.colors.base
    return {
      tooltip,
      modifiers,
      icon: 'fal fa-long-arrow-up',
      iconColor,
    }
  }, [isFirst, theme.colors.base, theme.colors.baseLighterS])

  const moveDownButton = useMemo(() => {
    const tooltip = isLast ? null : 'mover para baixo'
    const modifiers = isLast ? ['removeBorders', 'disabled'] : ['removeBorders']
    const iconColor = isLast ? theme.colors.baseLighterS : theme.colors.base
    return {
      tooltip,
      modifiers,
      icon: 'fal fa-long-arrow-down',
      iconColor,
    }
  }, [isLast, theme.colors.base, theme.colors.baseLighterS])

  return (
    <Wrapper>
      <PageAddModuleModal isOpen={isOpen} toggle={toggle} onSelect={handleAddModule} />
      <ModuleActionItem {...newModuleAboveButton} onClick={toggle} />
      <ModuleActionItem {...editingButton} onClick={isEditing ? onCloseEdit : onEdit} />
      <ModuleActionItem {...copyButton} onClick={onCopy} />
      <ModuleActionItem {...moveUpButton} onClick={isFirst ? () => {} : onMoveUp} />
      <ModuleActionItem {...moveDownButton} onClick={isLast ? () => {} : onMoveDown} />
      <Tooltip tooltip="remover" placement="bottom" delayShow={400}>
        <Button
          modifiers={['removeBorders']}
          onClick={onDelete}
        >
          <Icon icon="fal fa-trash" height={20} />
        </Button>
      </Tooltip>
    </Wrapper>
  )
};

const Wrapper = styled.div`
  display: flex;
  position: absolute;
  right: ${props => props.theme.spacing.xs};
  top: ${props => props.theme.spacing.xs};
  box-shadow: ${props => props.theme.shadows.card};
`;

ModuleActions.propTypes = {
  onEdit: PropTypes.func,
  onCloseEdit: PropTypes.func,
  isEditing: PropTypes.bool,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  onDelete: PropTypes.func,
  theme: themeType,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  sectionActive: PropTypes.string,
  onCopy: PropTypes.func,
};

ModuleActions.defaultProps = {
  onEdit: () => null,
  onCloseEdit: () => null,
  isEditing: false,
  onMoveUp: () => null,
  onMoveDown: () => null,
  onDelete: () => null,
  theme: {},
  isFirst: false,
  isLast: false,
  sectionActive: '',
  onCopy: () => null,
};


export default withTheme(ModuleActions);
